<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`" ref="rootElementRef" class="w-100"></div>
</template>

<script>
import { getDataUrl,getHexColorByPosition,measureText,formatCash,getCurrencies } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import * as d3v4 from "d3v4";

  export default {
    name: 'AgeProfileChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      legendTitle:{
        type:String,
        default: ""
      },
      legendSubtitle:{
        type:String,
        default: ""
      },
      chartData:{
        type: Array,
        default: () => {
          return []
        }
      },
      circleColor:{
        type: String,
        default: "position"
      },
      circleSize:{
        type: String,
        default: "marketValue"
      },
      line:{
        type: Array,
        default: () => {
          return ['time']
        }
      }
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {}
      }
    },
    computed: {
      SVG_WIDTH(){
        this.width;
        if(this.size){
          return this.size
        }else if(this.$refs.rootElementRef){
          return this.$refs.rootElementRef.getBoundingClientRect().width;
        }else if(document.getElementById(this.rootElementId) && document.getElementById(this.rootElementId).getBoundingClientRect()){
          return document.getElementById(this.rootElementId).getBoundingClientRect().width;
        }else{
          return 420;
        }
      },
      PITCH_WIDTH(){
        return this.SVG_WIDTH - this.CHART_MARGIN.left - this.CHART_MARGIN.right
      },
      PITCH_HEIGHT() {
        return this.PITCH_WIDTH * 0.6;
      },
      rootElementId(){
        return `ageProfileChart${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      CHART_MARGIN(){
        return this.margin ? this.margin : {top: this.SVG_WIDTH * 0.05, right: 0, bottom: this.SVG_WIDTH * 0.1125, left: this.SVG_WIDTH * 0.075};
      },
      POSITIONS(){
        return [
          {code:"playerRecruitment.radarTemplates.Goalkeeper",color:getHexColorByPosition(1)},
          {code:"playerRecruitment.radarTemplates.Defender",color:getHexColorByPosition(2)},
          {code:"playerRecruitment.radarTemplates.MidfielderDefensive",color:getHexColorByPosition(5)},
          {code:"playerRecruitment.radarTemplates.Midfielder",color:getHexColorByPosition(6)},
          {code:"playerRecruitment.radarTemplates.MidfielderOffensive",color:getHexColorByPosition(9)},
          {code:"playerRecruitment.radarTemplates.Forward",color:getHexColorByPosition(14)}
        ]
      },
      currentUserCurrency(){
        return this.$store.getters["auth/currency"];
      },
      currentUserRate(){
        let rate = this.currentUserCurrency === "EUR" ? 1 : this.$store.getters["exchange/rates"][this.$store.getters["auth/currency"]];
        return rate ? rate : 1;
      },
      currencies(){
        return getCurrencies();
      },
      currentUserCurrencySymbol(){
        let currentCurrencyObj = _.find(this.currencies,(c) => c.code === this.currentUserCurrency)
        return currentCurrencyObj.symbol;
      },
      darkMode(){
        return this.$vuetify.theme.dark;
      },
    },
    mounted() {
      this.width = null;
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.renderD3();
    },
    methods: {
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      },
      async renderD3(){
        d3v4.select(`#${this.rootElementId} > svg`).remove();
        d3.selectAll('.tooltip').remove();

        const tooltip = d3v4
          .select("body")
          .append("div")
          .attr("class", "tooltip no-arrow");

        var svg = d3v4.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", this.SVG_WIDTH)
          .attr("height", this.PITCH_HEIGHT)
          .style("background-color", this.darkMode ? "#333" : "#fff")
          .attr("class", "")
          .attr("id", `${this.rootElementId}SVG`)
          .style("font-family","NeueMontreal")

        this.renderChart(svg)

        this.renderLegend(svg);

        this.renderLogo(svg);

        let font = await loadFonts();
        copyAndAppendElement(svg.node(),font.node(),0,0);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", "NeueMontreal")
          .style("fill", fill)
          .text(text);
      },
      renderLegend(svg){
        let textColor = this.darkMode ? 'white' : 'black';
        this.writeText(svg, 0.0875 * this.PITCH_WIDTH , 0.0375 * this.PITCH_HEIGHT, "start", (0.016 * this.SVG_WIDTH) + "px",
        `${ this.legendTitle }`,textColor);
        this.writeText(svg, 0.0875 * this.PITCH_WIDTH , 0.0675 * this.PITCH_HEIGHT, "start", (0.014 * this.SVG_WIDTH) + "px",
        `${ this.legendSubtitle }`,textColor);

        this.writeText(svg, 0.92 * this.PITCH_WIDTH , 0.0375 * this.PITCH_HEIGHT, "end", (0.016 * this.SVG_WIDTH) + "px",
        `${ this.crossings.length } ${this.$t("team.crossings")}`,textColor);
        this.writeText(svg, 0.92 * this.PITCH_WIDTH , 0.0675 * this.PITCH_HEIGHT, "end", (0.014 * this.SVG_WIDTH) + "px",
        `${ _.filter(this.crossings,(c) => c.outcome).length } ${this.$t("eventChart.successful")}`,textColor);
      },
      renderChart(svg){
        let g = svg.append("g").attr("transform","translate(" + this.CHART_MARGIN.left + "," + this.CHART_MARGIN.top + ")")
        let marketValues = _.map(this.chartData,(d) => d.marketValue);
        let r = d3v4.scaleLinear()
          .domain([ _.min(marketValues), _.max(marketValues)])
          .range([this.SVG_WIDTH * 0.005,this.SVG_WIDTH * 0.01]);


        //AxisBottom
        let xDomain = [16,40]
        let xRange = xDomain[1] - xDomain[0];
        let axisPaddingX = 0.05
        let x = d3v4.scaleLinear()
          .domain([xDomain[0] - (xRange * axisPaddingX), xDomain[1] + (xRange * axisPaddingX)])
          .range([ 0, this.PITCH_WIDTH * 0.80])
        g.append("g")
          .attr("transform", `translate(0,${this.PITCH_HEIGHT - this.CHART_MARGIN.bottom})`)
          .attr("class",this.darkMode ? 'axisDark' : '')
          .call(d3v4.axisBottom(x))
          .append("text")
            .attr("y", -8)
            .attr("x",x(40) - 6)
            .style("text-anchor", "end")
            .attr("fill", this.darkMode ? "white" : "#5D6971")
            .text(this.$t("playerDetail.age"));

        //AxisLeft
        let yDomain = [100,0]
        let yRange = yDomain[1] - yDomain[0];
        let axisPaddingY = 0.125
        let y = d3v4.scaleLinear()
          .range([ 0, (this.PITCH_HEIGHT - this.CHART_MARGIN.bottom) ])
          .domain([yDomain[0] - (yRange * axisPaddingY),yDomain[1] + (yRange * axisPaddingY)])
        g.append("g")
          .attr("class",this.darkMode ? 'axisDark' : '')
          .call(d3v4.axisLeft(y).ticks(6).tickFormat(d => d + "%"))
          .append("text")
            .attr("transform", "rotate(-90)")
            .attr("y", 8)
            .attr("x", -6)
            .attr("dy", ".71em")
            .style("text-anchor", "end")
            .attr("fill", this.darkMode ? "white" : "#5D6971")
            .text(this.$t("playerDetail.playerUsage"));

        //Y GridLines
        g.append("g")
          .attr("class", "grid")
          .attr("transform", `translate(0,0)`)
          .call(d3v4.axisLeft(y).ticks(6)
              .tickSize(-(this.PITCH_WIDTH * 0.8))
              .tickFormat("")
          )
        //X GridLines
        g.append("g")
          .attr("class", "grid")
          .attr("transform", `translate(0,${this.PITCH_HEIGHT - this.CHART_MARGIN.bottom})`)
          .call(d3v4.axisBottom(x).ticks(9)
              .tickSize(-(this.PITCH_HEIGHT - this.CHART_MARGIN.bottom))
              .tickFormat("")
          )

        //Gradients
        let defs = g.append("defs");
        _.chain(this.chartData).map(d => d.fieldPositionId).uniq().forEach(p => {
          let linearGradient = defs
            .append("linearGradient")
            .attr("id", `linear-gradient-${p}`)

          // linearGradient.append("stop")
          //     .attr("offset", "0%")
          //     .attr("stop-color", "#000");

          linearGradient.append("stop")
              .attr("offset", "0%")
              .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" : "#fff");

          linearGradient.append("stop")
              .attr("offset", "100%")
              .attr("stop-opacity",0.45)
              .attr("stop-color", `${getHexColorByPosition(p)}`);

          let linearGradientRev = defs
            .append("linearGradient")
            .attr("id", `linear-gradient-${p}-rev`)

          // linearGradient.append("stop")
          //     .attr("offset", "0%")
          //     .attr("stop-color", "#000");
          linearGradientRev.append("stop")
              .attr("offset", "0%")
              .attr("stop-opacity",0.45)
              .attr("stop-color", `${getHexColorByPosition(p)}`);

          linearGradientRev.append("stop")
              .attr("offset", "100%")
              .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" : "#fff");

        }).value();
        let linearGradient = defs
          .append("linearGradient")
          .attr("id", `linear-gradient-grey`)

        linearGradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" : "#fff");

        linearGradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-opacity",0.45)
            .attr("stop-color", `rgba(0,0,0,0.5)`);

        let linearGradientRev = defs
          .append("linearGradient")
          .attr("id", `linear-gradient-grey-rev`)

        linearGradientRev.append("stop")
            .attr("offset", "0%")
            .attr("stop-opacity",0.45)
            .attr("stop-color", `rgba(0,0,0,0.5)`);

        linearGradientRev.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" : "#fff");


        //Age Lines years in team
        if(this.line.includes("time")){
          g.selectAll("age-lines-time").data(this.chartData)
          .enter()
          .append("rect")
            .attr("x", (d) => { return x(d.ageAtStart) })
            .attr("y", (d) => { return y(d.pctMin); })
            .attr("height",1)
            .attr("width",(d) => {
              let radius = this.circleSize === 'marketValue' ? r(d.marketValue) : this.SVG_WIDTH * 0.005
              return x(d.age) - x(d.ageAtStart) - radius
            })
            .attr("stroke", (d) => {
              if(this.circleColor === 'position'){
                return `url(#linear-gradient-${d.fieldPositionId})`
              }else if(this.circleColor === 'years'){
                return `url(#linear-gradient-${d.age - d.ageAtStart <= 1 ? "DC" : "MC"})`
              }else{
                return `url(#linear-gradient-grey)`
              }
            })
            .attr("stroke-width", "1px")
            .attr("stroke-linecap","round")
            .attr("rx","2")
            .attr("fill", "none");
        }
        //Age Lines contract end
        if(this.line.includes("contract")){
          g.selectAll("age-lines-contract").data(this.chartData)
          .enter()
          .append("rect")
            .attr("x", (d) => {
              let radius = this.circleSize === 'marketValue' ? r(d.marketValue) : this.SVG_WIDTH * 0.005
              return x(d.age) + radius
            })
            .attr("y", (d) => { return y(d.pctMin); })
            .attr("height",1)
            .attr("width",(d) => {
              let radius = this.circleSize === 'marketValue' ? r(d.marketValue) : this.SVG_WIDTH * 0.005
              return x(d.ageAtEnd) - x(d.age) - radius
            })
            .attr("stroke", (d) => {
              if(this.circleColor === 'position'){
                return `url(#linear-gradient-${d.fieldPositionId}-rev)`
              }else if(this.circleColor === 'years'){
                return `url(#linear-gradient-${d.age - d.ageAtStart <= 1 ? "DC" : "MC"}-rev)`
              }else{
                return `url(#linear-gradient-grey-rev)`
              }
            })
            .attr("stroke-width", "1px")
            .attr("stroke-linecap","round")
            .attr("rx","2")
            .attr("fill", "none");
        }

        //Age Circles
        g.selectAll("age-circles").data(this.chartData)
        .enter()
        .append("circle")
          .attr("cx", (d) => { return x(d.age); })
          .attr("cy", (d) => { return y(d.pctMin); })
          .attr("r", (d) =>  { return this.circleSize === 'marketValue' ? r(d.marketValue) : this.SVG_WIDTH * 0.005 })
          .attr("id", (d) =>  { return `circle-age-${d.playerId}` })
          .style("fill",(d) => {
            if(this.circleColor === 'position'){
              return getHexColorByPosition(d.fieldPositionId)
            }else if(this.circleColor === 'years'){
              return d.age - d.ageAtStart <= 1 ? "#487bb8" : "#4caf50"
            }else {
              return this.darkMode ? "rgba(255,255,255,1)" : "rgba(0,0,0,0.5)"
            }
          })
          .style("fill-opacity",0.725)
          .style("stroke",(d) => {
            if(this.circleColor === 'position'){
              return d.age - d.ageAtStart < 1 ? 'black' : getHexColorByPosition(d.fieldPositionId)
            }else if(this.circleColor === 'years'){
              return d.age - d.ageAtStart < 1 ? "black" : "#4caf50"
            }else {
              return d.age - d.ageAtStart < 1 ? "black" : "rgba(0,0,0,0.25)"
            }
          })
          .style("stroke-width",1.5)
          .style("stroke-opacity",1)
          .on('mouseenter',(d) => {
            let tooltipText= `
              <div style="display:flex;flex-direction:column;align-items:center;padding:5px; 15px;">
                <span style="font-weight:bold;">${d.playerName}</span>
                <div style="display:flex;align-items:center">
                  <span style="background:${getHexColorByPosition(d.fieldPositionId) || 'black'};color:white; font-weight:bold; font-size:0.75rem;padding: 2px 4px;border-radius:10%">${d.fieldPositionId || "N/A"}</span>
                  <span style="margin:0.5rem;font-size:0.9rem;">${d.age} ${this.$t("playerDetail.yearsOld")}</span>
                </div>
                <span style="font-size:0.925rem;">${d.minutesPlayed || 0} ${this.$t("table.playerAllGames.minsPlayed")} (${d.usage ? d.usage.toFixed(0) : 0}%)</span>
                <span style="font-size:0.8rem;">${(d.age - d.ageAtStart) || 1} ${this.$t("team.timeInTeam")} · <span style="font-weight:bold">${this.currentUserCurrencySymbol}${formatCash(d.marketValue * 1000,this.currentUserRate)}</span></span>
                ${ d.greatestContractDate ? `<span style="font-size:0.75rem;">${this.$t("playerScout.contractEnd")}: <span style="font-weight:bold">${d.greatestContractDate}</span></span>` : ''}
              </div>
            `
            let circle = d3v4.select(`#circle-age-${d.playerId}`).node();
            d3v4.select("body > .tooltip")
              .style("top", circle.getBoundingClientRect().top + window.scrollY + r(d.marketValue) + 15 + "px")
              .style("height","auto")
              .style("width","auto")
              .style("display", "inline-block")
              .style("left", () => {
                return `${circle.getBoundingClientRect().left  - (d3v4.select("body > .tooltip").node().getBoundingClientRect().width / 2)}px`
              })
              .style("font-size", "14px")
              .html(tooltipText);
          })
          .on('mouseout',(d) => {
            d3.select("body > .tooltip").style("display", "none");
          })

        //Age Player name Labels
        g.selectAll("age-circles-text").data(this.chartData)
        .enter()
        .append("text")
          .attr("x", (d) => { return x(d.age); })
          .attr("y", (d) => {
            return this.calculateTextCircleY(d,x,y,r);
          })
          .attr("text-anchor","middle")
          .style("font-size",this.SVG_WIDTH * 0.01)
          .style("font-weight",600)
          .style("fill",this.darkMode ? 'white' : "rgba(0,0,0,0.55)")
          .text((d) => {
            let textY = this.calculateTextCircleY(d,x,y,r);
            let minDiff = this.calculateMinDiff(d,x,y,r);
            let tooClose = _.some(minDiff,(m) =>
            (
              (Math.abs(m[0]) <= (this.SVG_WIDTH * 0.03) && m[1] >= 0 && m[1] <= (this.SVG_WIDTH * 0.03)))
             || (Math.abs(m[0]) <= (measureText(d.playerName,this.SVG_WIDTH * 0.01) * 0.75) && m[1] >= 0  && m[1] <= (this.SVG_WIDTH * 0.015))
            )

            return tooClose ? '' : d.playerName
          });
      },
      calculateMinDiff(d,x,y,r){
        return _.chain(this.chartData).filter(i => i.playerId != d.playerId)
        .map((i) => [x(d.age) - x(i.age), y(d.pctMin) - y(i.pctMin)])
        .value();
      },
      calculateTextCircleY(d,x,y,r){
        let minDiff = this.calculateMinDiff(d,x,y,r);
        let maxDiffY = _.chain(minDiff)
        .filter((m) => Math.abs(m[0]) <= (this.SVG_WIDTH * 0.03)
          && m[1] >= 0 && m[1] <= (this.SVG_WIDTH * 0.03))
        .map(m => m[1]).max().value();
        let index = _.findIndex(this.chartData,(o) => o.playerId === d.playerId)
        return (maxDiffY && maxDiffY >= 0) ?
          y(d.pctMin) + r(d.marketValue) + (this.PITCH_HEIGHT * 0.025) :
          y(d.pctMin) - r(d.marketValue) - (this.PITCH_HEIGHT * 0.01);
      },
      renderLegend(svg){
        //Title
        svg
          .append("text")
          .attr("x", this.CHART_MARGIN.left)
          .attr("y", this.CHART_MARGIN.top / 2)
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.025)
          .style("font-weight",600)
          .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
          .text(this.legendTitle);

        //Subtitle
        svg
          .append("text")
          .attr("x", this.CHART_MARGIN.left * 1.025)
          .attr("y",(this.CHART_MARGIN.top * 0.85))
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.012)
          .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
          .text(this.legendSubtitle);

        //Positions
        if(this.circleColor === 'position'){
          _.forEach(this.POSITIONS,(p,i) => {
            svg
              .append("circle")
              .attr("cx", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left)
              .attr("cy", this.CHART_MARGIN.top + (i * this.PITCH_HEIGHT * 0.05))
              .attr("r", 8)
              .style("fill",p.color)
              .style("fill-opacity",0.725)
              .style("stroke",p.color)
              .style("stroke-width",2)
              .style("stroke-opacity",1)

            svg
              .append("text")
              .attr("x", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left + 25)
              .attr("y",this.CHART_MARGIN.top + (i * this.PITCH_HEIGHT * 0.05) + 4)
              .attr("text-anchor","start")
              .style("font-size",this.SVG_WIDTH * 0.01)
              .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
              .text(this.$t(p.code));
          })
        }else if(this.circleColor === 'years'){
          //<= 1 years
          svg
            .append("circle")
            .attr("cx", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left)
            .attr("cy", this.CHART_MARGIN.top )
            .attr("r", 8)
            .style("fill","#487bb8")
            .style("fill-opacity",0.725)
            .style("stroke","#487bb8" )
            .style("stroke-width",2)
            .style("stroke-opacity",1)
          svg
            .append("text")
            .attr("x", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left + 25)
            .attr("y",this.CHART_MARGIN.top + 4)
            .attr("text-anchor","start")
            .style("font-size",this.SVG_WIDTH * 0.01)
            .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
            .text(`<= 1 ${this.$t("team.timeInTeam")}`);

          //> 1 years
          svg
            .append("circle")
            .attr("cx", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left)
            .attr("cy", this.CHART_MARGIN.top + (this.PITCH_HEIGHT * 0.05))
            .attr("r", 8)
            .style("fill","#4caf50")
            .style("fill-opacity",0.725)
            .style("stroke","#4caf50")
            .style("stroke-width",2)
            .style("stroke-opacity",1)
          svg
            .append("text")
            .attr("x", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left + 25)
            .attr("y",this.CHART_MARGIN.top + (this.PITCH_HEIGHT * 0.05) + 4)
            .attr("text-anchor","start")
            .style("font-size",this.SVG_WIDTH * 0.01)
            .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
            .text(`> 1 ${this.$t("team.timeInTeam")}`);
        }

        if(this.circleColor != 'none'){
          let yOffset = this.circleColor === 'position' ? 6 : 2
          svg.append("line")
            .attr("x1",(this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left)
            .attr("y1",this.CHART_MARGIN.top + (yOffset * this.PITCH_HEIGHT * 0.05))
            .attr("x2",(this.PITCH_WIDTH * 0.96) + this.CHART_MARGIN.left)
            .attr("y2",this.CHART_MARGIN.top + (yOffset * this.PITCH_HEIGHT * 0.05))
            .style("stroke","grey")
            .style("stroke-width",1)
        }


        let yOffsetNewSignings = this.circleColor === 'position' ? 7 : this.circleColor === 'years' ? 3 : 0
        svg
          .append("circle")
          .attr("cx", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left)
          .attr("cy", this.CHART_MARGIN.top + (yOffsetNewSignings * this.PITCH_HEIGHT * 0.05))
          .attr("r", 8)
          .style("fill","transparent")
          .style("fill-opacity",0.725)
          .style("stroke","black")
          .style("stroke-width",2)
          .style("stroke-opacity",1)

        svg
          .append("text")
          .attr("x", (this.PITCH_WIDTH * 0.825) + this.CHART_MARGIN.left + 25)
          .attr("y",this.CHART_MARGIN.top + (yOffsetNewSignings * this.PITCH_HEIGHT * 0.05) + 4)
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.01)
          .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
          .text(this.$t("team.newSignings"));

        //Size by marketValue
        if(this.circleSize != 'none'){
          let yOffsetCircleSize = this.circleColor === 'position' ? 6.25 : this.circleColor === 'years' ? 3.25 : 0
          svg
            .append("circle")
            .attr("cx", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left)
            .attr("cy", this.CHART_MARGIN.top + (yOffsetCircleSize * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.15))
            .attr("r", this.SVG_WIDTH * 0.0125)
            .style("fill",this.darkMode ? "rgb(255,255,255,0.1)" : "rgb(0,0,0,0.3)")
            .style("fill-opacity",1)
            .style("stroke","rgb(0,0,0,0.5)")
            .style("stroke-width",2)
            .style("stroke-opacity",0.725)
          svg
            .append("text")
            .attr("x", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left)
            .attr("y",this.CHART_MARGIN.top + (yOffsetCircleSize * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.2))
            .attr("text-anchor","middle")
            .style("font-size",this.SVG_WIDTH * 0.01)
            .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
            .text(this.$t("team.sizeByMarketValue"));
        }

        //Time in club
        let linearGradient = svg.append("defs")
            .append("linearGradient")
            .attr("id", `linear-gradient-legend`)
        linearGradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color",this.darkMode ? "rgba(255,255,255,0.69)" : "#fff");
        linearGradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-opacity",0.45)
            .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" :`rgba(0,0,0,0.69)`);

        let linearGradientRev = svg.append("defs")
            .append("linearGradient")
            .attr("id", `linear-gradient-legend-rev`)
        linearGradientRev.append("stop")
            .attr("offset", "0%")
            .attr("stop-opacity",0.45)
            .attr("stop-color", this.darkMode ? "rgba(255,255,255,0.33)" :`rgba(0,0,0,0.69)`);
        linearGradientRev.append("stop")
            .attr("offset", "100%")
            .attr("stop-color",this.darkMode ? "rgba(255,255,255,0.69)" : "#fff");
        if(this.line.includes('time')){
          let yOffsetGradient1 = this.circleColor === 'position' ? 4.5 : this.circleColor === 'years' ? 2.5 : -1;
          svg
            .append("rect")
            .attr("x", (this.PITCH_WIDTH * 0.85) + this.CHART_MARGIN.left)
            .attr("y", this.CHART_MARGIN.top + (yOffsetGradient1 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.35))
            .attr("height",1)
            .attr("width",(this.PITCH_WIDTH * 0.905) - (this.PITCH_WIDTH * 0.85))
            .attr("stroke", (d) => `url(#linear-gradient-legend)`)
            .attr("stroke-width", "1px")
            .attr("stroke-linecap","round")
            .attr("rx","2")
            .attr("fill", "none");
          svg
            .append("circle")
            .attr("cx", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left )
            .attr("cy", this.CHART_MARGIN.top + (yOffsetGradient1 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.35))
            .attr("r", this.SVG_WIDTH * 0.0065)
            .style("fill",this.darkMode ? "rgba(255,255,255,0.66)" : "rgb(0,0,0,0.9)")
            .style("fill-opacity",1)
            .style("stroke","rgb(0,0,0,0.5)")
            .style("stroke-width",2)
            .style("stroke-opacity",0.725)
          svg
            .append("text")
            .attr("x", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left)
            .attr("y",this.CHART_MARGIN.top + (yOffsetGradient1 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.40))
            .attr("text-anchor","middle")
            .style("font-size",this.SVG_WIDTH * 0.01)
            .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
            .text(this.$t("team.timeInTeam"));
        }
        if(this.line.includes('contract')){
          let yOffsetGradient2 = this.circleColor === 'position' ? 6.5 : this.circleColor === 'years' ? 4.5 : 1;
          svg
            .append("rect")
            .attr("x", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left + (this.SVG_WIDTH * 0.0065))
            .attr("y", this.CHART_MARGIN.top + (yOffsetGradient2 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.35))
            .attr("height",1)
            .attr("width",(this.PITCH_WIDTH * 0.90) - (this.PITCH_WIDTH * 0.85))
            .attr("stroke", (d) => `url(#linear-gradient-legend-rev)`)
            .attr("stroke-width", "1px")
            .attr("stroke-linecap","round")
            .attr("rx","2")
            .attr("fill", "none");
          svg
            .append("circle")
            .attr("cx", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left )
            .attr("cy", this.CHART_MARGIN.top + (yOffsetGradient2 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.35))
            .attr("r", this.SVG_WIDTH * 0.0065)
            .style("fill",this.darkMode ? "rgba(255,255,255,0.66)" : "rgb(0,0,0,0.9)")
            .style("fill-opacity",1)
            .style("stroke","rgb(0,0,0,0.5)")
            .style("stroke-width",2)
            .style("stroke-opacity",0.725)
          svg
            .append("text")
            .attr("x", (this.PITCH_WIDTH * 0.905) + this.CHART_MARGIN.left)
            .attr("y",this.CHART_MARGIN.top + (yOffsetGradient2 * this.PITCH_HEIGHT * 0.05) + (this.PITCH_HEIGHT * 0.40))
            .attr("text-anchor","middle")
            .style("font-size",this.SVG_WIDTH * 0.01)
            .style("fill",this.darkMode ? "white" : "rgba(0,0,0,0.69)")
            .text(this.$t("playerScout.contractEnd"));
        }
      },
      renderLogo(svg){
        // Draw Driblab Logo
        const logoWidth = this.SVG_WIDTH * 0.18;
        const logoHeight = this.PITCH_HEIGHT * 0.1;
        let base_image = new Image();
        base_image.src = require(`@/assets/img/driblab_${this.darkMode ? 'white' : 'color'}.png`);
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          let imgNode = svg.append("svg:image")
          .attr("x", this.SVG_WIDTH * 0.83)
          .attr("y", this.PITCH_HEIGHT * 0.85)
          .attr("width", logoWidth * 0.8)
          .attr("height", logoHeight * 0.8)
          .attr("xlink:href", dataUrl);
        }
      }
    },
    destroyed() {
      delete this.observer;
      window.removeEventListener("resize",this.render);
    },
  }

</script>

<style lang="scss">
.line {
  fill: none;
  stroke: steelblue;
  stroke-width: 2px;
}

.grid line {
  stroke: lightgrey;
  stroke-opacity: 0.7;
  shape-rendering: crispEdges;
}

.grid path {
  stroke-width: 0;
}

.axisDark path,
.axisDark line {
  fill: none;
  stroke: white;
  shape-rendering: crispEdges;
}
.axisDark text {
  fill: white;
}

</style>



// WEBPACK FOOTER //
// src/views/base/AgeProfileChart.vue